import _ from 'lodash';
import type { ColDef } from 'ag-grid-community';

// The bits of AG Grid's params objects (ICellRendererParams, IHeaderParams,
// ValueGetterParams...) that our existing renderers actually read. DataTable
// hands these to the AG-era renderers so they run unchanged. Anything a
// renderer reaches for beyond this surface is a migration item (see the
// renderer audit in the spike write-up).

type RowEvent = 'mouseEnter' | 'mouseLeave' | 'rowSelected';
type Listener = () => void;

// Per-row listeners, fired from the <tr>'s own mouse events. EntityLink (and
// the board cells) only show their buttons while the row is hovered, which
// they learn through node.addEventListener('mouseEnter' | 'mouseLeave').
export class RowEventBus {
  private listeners = new Map<string, Map<RowEvent, Set<Listener>>>();

  add({
    rowId,
    event,
    listener,
  }: {
    rowId: string;
    event: RowEvent;
    listener: Listener;
  }) {
    const forRow = this.listeners.get(rowId) ?? new Map();
    const forEvent = forRow.get(event) ?? new Set();
    forEvent.add(listener);
    forRow.set(event, forEvent);
    this.listeners.set(rowId, forRow);
  }

  remove({
    rowId,
    event,
    listener,
  }: {
    rowId: string;
    event: RowEvent;
    listener: Listener;
  }) {
    this.listeners.get(rowId)?.get(event)?.delete(listener);
  }

  emit({ rowId, event }: { rowId: string; event: RowEvent }) {
    this.listeners
      .get(rowId)
      ?.get(event)
      ?.forEach((listener) => listener());
  }
}

export type ShimSortModel = { colId: string; sort: 'asc' | 'desc' }[];

// sortChanged: a header following the table's sort. modelUpdated: the rows
// changed (a page loaded, a sort, an edit). firstDataRendered: the first
// rows are drawn. selectionChanged: rows were selected or deselected.
// column*: columns were moved, shown or hidden, resized, or replaced.
// cellClicked: a cell was clicked
type TableEvent =
  | 'sortChanged'
  | 'modelUpdated'
  | 'firstDataRendered'
  | 'selectionChanged'
  | 'columnMoved'
  | 'columnVisible'
  | 'columnResized'
  | 'columnEverythingChanged'
  | 'cellClicked';
// Called with AG's event object ({ type, api, ...what the event carries }),
// which owners read the api from (Board's drill highlight, DimensionExplorer)
type TableListener = (event: {
  type: TableEvent;
  api: unknown;
  [key: string]: unknown;
}) => void;

// Grid-level listeners (api.addEventListener)
export class TableEventBus {
  private listeners = new Map<TableEvent, Set<TableListener>>();

  private hasRenderedData = false;

  private api: unknown;

  add({ event, listener }: { event: TableEvent; listener: TableListener }) {
    const forEvent = this.listeners.get(event) ?? new Set();
    forEvent.add(listener);
    this.listeners.set(event, forEvent);
    // An owner gets the api once the table mounts, and may only listen after
    // the first rows are drawn (rows already loaded). It still hears about
    // them, a moment later, as an AG listener added before the first page
    // arrives would.
    if (event === 'firstDataRendered' && this.hasRenderedData) {
      setTimeout(() => {
        if (this.listeners.get(event)?.has(listener)) {
          listener({ type: event, api: this.api });
        }
      });
    }
  }

  remove({ event, listener }: { event: TableEvent; listener: TableListener }) {
    this.listeners.get(event)?.delete(listener);
  }

  setApi(api: unknown) {
    this.api = api;
  }

  getApi() {
    return this.api;
  }

  emit(event: TableEvent, details: { [key: string]: unknown } = {}) {
    if (event === 'firstDataRendered') {
      this.hasRenderedData = true;
    }
    this.listeners
      .get(event)
      ?.forEach((listener) =>
        listener({ ...details, type: event, api: this.api }),
      );
  }
}

// AG's column state, as owners save and apply it (Explore's saved layout).
// The fields DataTable keeps; AG's others (pinned, flex, aggFunc...) are
// ignored when applied
export type ShimColumnState = {
  colId: string;
  width?: number;
  hide?: boolean | null;
  sort?: 'asc' | 'desc' | null;
  sortIndex?: number | null;
};

// AG's export params, as owners pass them to exportDataAsExcel/Csv
export type ShimExportParams = {
  fileName?: string;
  sheetName?: string;
  onlySelected?: boolean;
  columnKeys?: (string | { getColId: () => string })[];
  skipPinnedBottom?: boolean;
  processCellCallback?: DataTable.ProcessExportCell;
};

// AG's showContextMenu params, and what a right-click opens the menu with
export interface ContextMenuRequest {
  node: NodeShim;
  column: ColumnShim | null;
  value: unknown;
  x: number;
  y: number;
}

// What the shims need from the mounted table. Its fields are reassigned on
// every render, so a renderer holding on to a shim always sees live state.
export interface TableBridge {
  getSortModel: () => ShimSortModel;
  setSortModel: (sortModel: ShimSortModel) => void;
  isRowSelected: (rowId: string) => boolean;
  setRowSelected: ({
    rowId,
    isSelected,
    clearOthers,
  }: {
    rowId: string;
    isSelected: boolean;
    clearOthers?: boolean;
  }) => void;
  // Every column, shown or hidden, in the order shown, as AG's
  // api.getColumnState() lists them
  getColumnState: () => ShimColumnState[];
  // Where columns show, whether they're hidden, and their widths (AG's
  // applyColumnState; the sort is applied through setSortModel)
  applyColumnLayout: (args: {
    state: ShimColumnState[];
    applyOrder: boolean;
    defaultHide?: boolean;
  }) => void;
  // Every leaf column, in the order shown (AG's api.getColumns), and whether
  // one is shown
  getColumns: () => ColumnShim[];
  isColumnVisible: (colId: string) => boolean;
  // A leaf column by id, shown or hidden (AG's api.getColumn)
  getColumn: (colId: string) => ColumnShim | null;
  deselectAll: () => void;
  getSelectedRows: () => DataTable.Row[];
  // AG's gridOptions.defaultColDef, which an owner may change (Explore adds a
  // tooltip to every column)
  getDefaultColDef: () => ColDef | undefined;
  setDefaultColDef: (defaultColDef: ColDef) => void;
  // AG's client-side exports, built on the table's export cells
  exportDataAsExcel: (params: ShimExportParams) => void;
  exportDataAsCsv: (params: ShimExportParams) => void;
  getDataAsCsv: (params: ShimExportParams) => string;
  // Replace a row's data with what `change` makes of it (node.updateData,
  // node.setDataValue, a cell editor's value)
  updateRowData: ({
    rowId,
    change,
  }: {
    rowId: string;
    change: (data: DataTable.Row) => DataTable.Row;
  }) => void;
  // Every loaded row (AG's api.forEachNode)
  forEachNode: (callback: (node: NodeShim, index: number) => void) => void;
  // Bring a row into view, centred (AG's api.ensureNodeVisible)
  scrollToRow: (rowId: string) => void;
  showContextMenu: (request: ContextMenuRequest) => void;
  // Close the open cell editor, keeping its value unless cancelled
  stopEditing: (isCancelled?: boolean) => void;
  // AG's setGridOption('context') and redrawRows: rows draw again with the
  // context their class rules and cells now read
  setContext: (context: { [key: string]: any }) => void;
  redrawRows: () => void;
  isDestroyed: boolean;
  gridOptions: { [key: string]: any };
  // AG's gridOptions.context. One stable object for the table's lifetime:
  // DeltaHeaderCell writes `context.isAbsolute` into it
  context: { [key: string]: any };
  events: RowEventBus;
  tableEvents: TableEventBus;
}

// A bridge with no table behind it (the headless CSV builder, and a mounted
// table's bridge before its first render fills it in)
export const buildInertBridge = (
  overrides: Partial<TableBridge> = {},
): TableBridge => ({
  getSortModel: () => [],
  setSortModel: () => undefined,
  isRowSelected: () => false,
  setRowSelected: () => undefined,
  getColumnState: () => [],
  applyColumnLayout: () => undefined,
  getColumns: () => [],
  isColumnVisible: () => true,
  getColumn: () => null,
  deselectAll: () => undefined,
  getSelectedRows: () => [],
  getDefaultColDef: () => undefined,
  setDefaultColDef: () => undefined,
  exportDataAsExcel: () => undefined,
  exportDataAsCsv: () => undefined,
  getDataAsCsv: () => '',
  updateRowData: () => undefined,
  forEachNode: () => undefined,
  scrollToRow: () => undefined,
  showContextMenu: () => undefined,
  stopEditing: () => undefined,
  setContext: () => undefined,
  redrawRows: () => undefined,
  isDestroyed: false,
  gridOptions: {},
  context: {},
  events: new RowEventBus(),
  tableEvents: new TableEventBus(),
  ...overrides,
});

export interface NodeShim {
  id: string;
  data: DataTable.Row;
  rowIndex: number | null;
  rowPinned: 'bottom' | null;
  isRowPinned: () => boolean;
  isSelected: () => boolean;
  setSelected: (isSelected: boolean, clearOthers?: boolean) => void;
  // AG's node.setData / updateData: the row's data becomes `data`
  setData: (data: DataTable.Row) => void;
  updateData: (data: DataTable.Row) => void;
  // AG's node.setDataValue: one column's field gets `value`
  setDataValue: (
    colKey: string | { getColId: () => string },
    value: unknown,
  ) => void;
  addEventListener: (event: RowEvent, listener: Listener) => void;
  removeEventListener: (event: RowEvent, listener: Listener) => void;
}

// The field a column reads and writes (AG's setDataValue writes the column's
// field, which defaults to its id)
export const getColumnField = ({
  bridge,
  colId,
}: {
  bridge: TableBridge;
  colId: string;
}) => bridge.getColumn(colId)?.getColDef().field ?? colId;

export const buildNodeShim = ({
  bridge,
  rowId,
  data,
  rowIndex,
  isPinned,
}: {
  bridge: TableBridge;
  rowId: string;
  data: DataTable.Row;
  rowIndex: number | null;
  isPinned: boolean;
}): NodeShim => {
  const replaceData = (next: DataTable.Row) => {
    if (!isPinned) {
      bridge.updateRowData({ rowId, change: () => next });
    }
  };
  return {
    id: rowId,
    data,
    rowIndex,
    rowPinned: isPinned ? 'bottom' : null,
    isRowPinned: () => isPinned,
    isSelected: () => bridge.isRowSelected(rowId),
    setSelected: (isSelected, clearOthers) =>
      bridge.setRowSelected({ rowId, isSelected, clearOthers }),
    setData: replaceData,
    updateData: replaceData,
    setDataValue: (colKey, value) => {
      if (isPinned) {
        return;
      }
      const colId = typeof colKey === 'string' ? colKey : colKey.getColId();
      const field = getColumnField({ bridge, colId });
      bridge.updateRowData({
        rowId,
        change: (current) => ({ ...current, [field]: value }),
      });
    },
    addEventListener: (event, listener) =>
      bridge.events.add({ rowId, event, listener }),
    removeEventListener: (event, listener) =>
      bridge.events.remove({ rowId, event, listener }),
  };
};

export interface ColumnShim {
  getColId: () => string;
  getId: () => string;
  getSort: () => 'asc' | 'desc' | undefined;
  getColDef: () => ColDef;
  getUserProvidedColDef: () => ColDef | null;
  isVisible: () => boolean;
}

export const buildColumnShim = ({
  bridge,
  colId,
  colDef,
  userColDef,
}: {
  bridge: TableBridge;
  colId: string;
  colDef: ColDef;
  userColDef: ColDef;
}): ColumnShim => ({
  getColId: () => colId,
  getId: () => colId,
  getSort: () => bridge.getSortModel().find((s) => s.colId === colId)?.sort,
  getColDef: () => colDef,
  getUserProvidedColDef: () => userColDef,
  isVisible: () => bridge.isColumnVisible(colId),
});

// Methods a renderer may look up without calling (React, promise sniffing,
// JSON) must not hit the "unsupported" trap below
const PASS_THROUGH_PROPS = ['then', 'toJSON', '$$typeof', 'constructor'];

// An AG api call the shim doesn't cover: a migration item, so it fails loudly
// everywhere but production
const unsupported = (what: string) => {
  if (process.env.NODE_ENV === 'production') {
    return undefined;
  }
  throw new Error(
    `DataTable: api.${what} is not supported by the AG Grid shim`,
  );
};

export const buildApiShim = (bridge: TableBridge) => {
  // Loosely typed: it stands in for whatever AG api a renderer or owner
  // reads (see onGridReady)
  const api: { [key: string]: any } = {
    // As AG's: a column's sort, visibility and width change only where the
    // state says (undefined leaves it), and applyOrder puts the columns in
    // the state's order
    applyColumnState: ({
      state = [],
      applyOrder,
      defaultState,
    }: {
      state?: ShimColumnState[];
      applyOrder?: boolean;
      defaultState?: { sort?: null; hide?: boolean };
    }) => {
      const sorted = state.filter((s) => s.sort !== undefined);
      const isSortReset = !!defaultState && defaultState.sort === null;
      if (sorted.length > 0 || isSortReset) {
        const base = isSortReset
          ? []
          : bridge
              .getSortModel()
              .filter((s) => !sorted.some((st) => st.colId === s.colId));
        const applied = _.sortBy(
          sorted.filter((s) => !!s.sort),
          (s) => s.sortIndex ?? Number.MAX_SAFE_INTEGER,
        ).map((s) => ({ colId: s.colId, sort: s.sort as 'asc' | 'desc' }));
        const next = [...applied, ...base];
        if (!_.isEqual(next, bridge.getSortModel())) {
          bridge.setSortModel(next);
        }
      }
      bridge.applyColumnLayout({
        state,
        applyOrder: !!applyOrder,
        defaultHide: defaultState?.hide,
      });
      return true;
    },
    getGridOption: (key: string) => {
      if (key === 'context') {
        return bridge.context;
      }
      if (key === 'defaultColDef') {
        return bridge.getDefaultColDef();
      }
      return bridge.gridOptions[key];
    },
    setGridOption: (key: string, value: unknown) => {
      if (key === 'context') {
        bridge.setContext((value ?? {}) as { [key: string]: any });
        return;
      }
      if (key === 'defaultColDef') {
        bridge.setDefaultColDef((value ?? {}) as ColDef);
        return;
      }
      unsupported(`setGridOption('${key}')`);
    },
    getColumnState: () => bridge.getColumnState(),
    getColumn: (colId: string) => bridge.getColumn(colId),
    getColumns: () => bridge.getColumns(),
    getAllDisplayedColumns: () =>
      bridge.getColumns().filter((column) => column.isVisible()),
    deselectAll: () => bridge.deselectAll(),
    getSelectedRows: () => bridge.getSelectedRows(),
    exportDataAsExcel: (params?: ShimExportParams) =>
      bridge.exportDataAsExcel(params ?? {}),
    exportDataAsCsv: (params?: ShimExportParams) =>
      bridge.exportDataAsCsv(params ?? {}),
    getDataAsCsv: (params?: ShimExportParams) =>
      bridge.getDataAsCsv(params ?? {}),
    // AG's server-side model would ask its datasource again; DataTable's rows
    // are its owner's, who reloads them when they change
    refreshServerSide: () => undefined,
    addEventListener: (event: TableEvent, listener: TableListener) =>
      bridge.tableEvents.add({ event, listener }),
    removeEventListener: (event: TableEvent, listener: TableListener) =>
      bridge.tableEvents.remove({ event, listener }),
    isDestroyed: () => bridge.isDestroyed,
    refreshCells: () => undefined,
    redrawRows: () => bridge.redrawRows(),
    forEachNode: (callback: (node: NodeShim, index: number) => void) =>
      bridge.forEachNode(callback),
    ensureNodeVisible: (node: NodeShim | null | undefined) => {
      if (node) {
        bridge.scrollToRow(node.id);
      }
    },
    showContextMenu: ({
      rowNode,
      column,
      value,
      x,
      y,
    }: {
      rowNode?: NodeShim | null;
      column?: ColumnShim | null;
      value?: unknown;
      x?: number;
      y?: number;
    }) => {
      if (rowNode) {
        bridge.showContextMenu({
          node: rowNode,
          column: column ?? null,
          value,
          x: x ?? 0,
          y: y ?? 0,
        });
      }
    },
    stopEditing: (isCancelled?: boolean) => bridge.stopEditing(isCancelled),
    // DataTable sizes its own columns (and refits them as its container
    // resizes), so AG's one-off sizing calls have nothing to do
    sizeColumnsToFit: () => undefined,
    autoSizeAllColumns: () => undefined,
    autoSizeColumns: () => undefined,
  };

  return new Proxy(api, {
    get: (target, prop) => {
      if (typeof prop === 'symbol' || PASS_THROUGH_PROPS.includes(prop)) {
        return target[prop as string];
      }
      // As AG's api once its grid is destroyed: calls do nothing (so an
      // owner holding the old api, as Explore's column manager may across a
      // remount, waits for the new one)
      if (bridge.isDestroyed && prop !== 'isDestroyed') {
        return () => undefined;
      }
      if (prop in target) {
        return target[prop];
      }
      return () => unsupported(prop);
    },
  });
};

export const sortModelFromSorting = (
  sorting: { id: string; desc: boolean }[],
): ShimSortModel =>
  sorting.map((s) => ({ colId: s.id, sort: s.desc ? 'desc' : 'asc' }));

export const sortingFromSortModel = (sortModel: ShimSortModel) =>
  sortModel.map((s) => ({ id: s.colId, desc: s.sort === 'desc' }));
